import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ShieldWarning, ListChecks, CheckCircle, Sparkle, Printer, Plus, Trash } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";

// Safety Plan Worksheet — DRAFT hazard analysis generated from formulation data.
// HARD RULE: every view + export carries the "for QA review, not audit-ready"
// disclaimer. Nothing is "approved" until a NAMED reviewer signs off. The
// generator only PRE-FILLS suggestions; QA edits/confirms everything here.

// Carried VERBATIM on every worksheet view + export. Keep byte-for-byte identical
// to SAFETY_PLAN_DISCLAIMER in artifacts/alaines-bench-api/safety_plan.py.
const DISCLAIMER =
  "Draft hazard analysis worksheet — for QA review. Not an audit-ready HACCP plan.";

// How the finished product is distributed — drives which steps are genuine CCPs.
// Values must match _DISTRIBUTION_MODES in artifacts/alaines-bench-api/safety_plan.py.
const DISTRIBUTION_OPTIONS = [
  { value: "", label: "— Select how it's distributed —" },
  { value: "refrigerated", label: "Refrigerated (cold chain)" },
  { value: "frozen", label: "Frozen" },
  { value: "ambient", label: "Ambient / shelf-stable" },
  { value: "retort", label: "Retort / low-acid canned" },
  { value: "hot_fill", label: "Hot-fill-and-hold" },
];

const linesToArray = (s) =>
  (s || "")
    .split("\n")
    .map((x) => x.trim())
    .filter(Boolean);
const arrayToLines = (a) => (Array.isArray(a) ? a.join("\n") : a || "");

const STATUS_META = {
  draft: { label: "Draft", cls: "bg-stone-100 text-stone-700 border-stone-300" },
  in_review: { label: "In review", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  approved: { label: "Approved", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
};

function fmt(ts) {
  if (!ts) return "";
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return ts;
  }
}

const esc = (s) =>
  String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

export default function SafetyPlanPanel({
  recipeId,
  productName,
  productType,
  ph,
  setPh,
  waterActivity,
  setWaterActivity,
  thermalTempF,
  setThermalTempF,
  thermalHoldMin,
  setThermalHoldMin,
  acidified,
  setAcidified,
  killStep,
  setKillStep,
  distributionMode,
  setDistributionMode,
  safetyPlan,
  setSafetyPlan,
  onSave,
  isDemo = false,
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reviewerName, setReviewerName] = useState("");

  // Wet savory/beverage products and dry blends retain their established lanes.
  // Broad baked and dairy/plant categories remain product-specific: they collect
  // useful process inputs without inferring wet-acidified or low-moisture status.
  const isWet = ["wet_sauce", "broth", "wet_marinade", "dressing", "beverage"].includes(productType);
  const isCategoryNeutral = ["baked_goods", "dairy_plant"].includes(productType);
  const assessmentHeading = isCategoryNeutral
    ? "Product-Specific Process Assessment"
    : isWet
      ? "Acidified / Thermal Assessment"
      : "Low-Moisture / Preventive Controls (HARPC) Assessment";

  // Local editable mirror of the worksheet (textareas hold newline-joined arrays).
  const [draft, setDraft] = useState(null);

  useEffect(() => {
    if (!safetyPlan) {
      setDraft(null);
      return;
    }
    setDraft({
      allergen_control: {
        declared_allergens: safetyPlan.allergen_control?.declared_allergens || [],
        hidden_allergens: safetyPlan.allergen_control?.hidden_allergens || [],
        cross_contact: arrayToLines(safetyPlan.allergen_control?.cross_contact),
        control_measures: arrayToLines(safetyPlan.allergen_control?.control_measures),
      },
      ingredient_hazards: (safetyPlan.ingredient_hazards || []).map((r) => ({
        ingredient: r.ingredient || "",
        hazards: arrayToLines(r.hazards),
      })),
      acidified_foods: {
        ...(safetyPlan.acidified_foods || {}),
        hazards: arrayToLines(safetyPlan.acidified_foods?.hazards),
        control_points: arrayToLines(safetyPlan.acidified_foods?.control_points),
        notes: arrayToLines(safetyPlan.acidified_foods?.notes),
      },
      hazard_analysis: (safetyPlan.hazard_analysis || []).map((r) => ({
        step: r.step || "",
        bio: arrayToLines(r.bio),
        chem: arrayToLines(r.chem),
        phys: arrayToLines(r.phys),
        controls: arrayToLines(r.controls),
        ccp: !!r.ccp,
        ccp_reason: r.ccp_reason || "",
      })),
      notes: safetyPlan.notes || "",
    });
  }, [safetyPlan]);

  const status = safetyPlan?.status || "draft";
  const meta = STATUS_META[status] || STATUS_META.draft;

  // ── Generate / regenerate ────────────────────────────────────────────────
  const generate = async () => {
    if (!recipeId) {
      toast.message("Save this formulation first", {
        description: "Create the formulation, then reopen it to generate a worksheet.",
      });
      if (onSave) await onSave();
      return;
    }
    setBusy(true);
    try {
      if (onSave) await onSave(); // persist pH / aw / thermal before generating
      const { data } = await api.post(`/recipes/${recipeId}/safety-plan/generate`);
      setSafetyPlan(data);
      setOpen(true);
      toast.success("Draft worksheet generated — review every line before sign-off.");
    } catch (e) {
      if (!e.handledToast) toast.error("Could not generate the worksheet.");
    } finally {
      setBusy(false);
    }
  };

  // ── Save QA edits ─────────────────────────────────────────────────────────
  const saveEdits = async () => {
    if (!recipeId || !draft) return;
    setBusy(true);
    try {
      const payload = {
        allergen_control: {
          declared_allergens: draft.allergen_control.declared_allergens,
          hidden_allergens: draft.allergen_control.hidden_allergens,
          cross_contact: linesToArray(draft.allergen_control.cross_contact),
          control_measures: linesToArray(draft.allergen_control.control_measures),
        },
        ingredient_hazards: draft.ingredient_hazards.map((r) => ({
          ingredient: r.ingredient,
          hazards: linesToArray(r.hazards),
        })),
        acidified_foods: {
          ...draft.acidified_foods,
          hazards: linesToArray(draft.acidified_foods.hazards),
          control_points: linesToArray(draft.acidified_foods.control_points),
          notes: linesToArray(draft.acidified_foods.notes),
        },
        hazard_analysis: draft.hazard_analysis.map((r) => ({
          step: r.step,
          bio: linesToArray(r.bio),
          chem: linesToArray(r.chem),
          phys: linesToArray(r.phys),
          controls: linesToArray(r.controls),
          ccp: !!r.ccp,
          ccp_reason: r.ccp_reason || "",
        })),
        notes: draft.notes,
      };
      const { data } = await api.put(`/recipes/${recipeId}/safety-plan`, payload);
      setSafetyPlan(data);
      toast.success("Edits saved — worksheet returned to “In review”.");
    } catch (e) {
      if (!e.handledToast) toast.error("Could not save edits.");
    } finally {
      setBusy(false);
    }
  };

  // ── Named-reviewer approval ───────────────────────────────────────────────
  const approve = async () => {
    if (!recipeId) return;
    const name = reviewerName.trim();
    if (!name) return toast.error("Enter the reviewer's name to sign off.");
    setBusy(true);
    try {
      const { data } = await api.post(`/recipes/${recipeId}/safety-plan/approve`, {
        reviewer_name: name,
      });
      setSafetyPlan(data);
      setReviewerName("");
      toast.success(`Approved by ${name}.`);
    } catch (e) {
      if (!e.handledToast) toast.error("Could not record approval.");
    } finally {
      setBusy(false);
    }
  };

  // ── Print / export (carries disclaimer top + bottom) ──────────────────────
  const printWorksheet = () => {
    if (isDemo) {
      toast.info("Printing and export are not available in the demo. Sign in to print or export the safety plan.");
      return;
    }
    if (!safetyPlan) return;
    const ac = safetyPlan.allergen_control || {};
    const af = safetyPlan.acidified_foods || {};
    const ih = safetyPlan.ingredient_hazards || [];
    const rows = safetyPlan.hazard_analysis || [];
    const planIsNeutral = af.applicable === false || af.classification === "product_specific";
    const planIsWet = !planIsNeutral && !af.low_moisture;
    const printedAssessmentHeading = planIsNeutral
      ? "Product-Specific Process Assessment"
      : planIsWet
        ? "Acidified / Thermal Assessment"
        : "Low-Moisture / Preventive Controls (HARPC) Assessment";
    const list = (a) =>
      (a && a.length
        ? `<ul>${a.map((x) => `<li>${esc(x)}</li>`).join("")}</ul>`
        : `<p class="muted">None noted — QA to confirm.</p>`);
    const reviewLine =
      status === "approved"
        ? `Approved by ${esc(safetyPlan.reviewer_name)} · ${esc(fmt(safetyPlan.approved_at))}`
        : `Status: ${esc(STATUS_META[status]?.label || status)} — not yet approved`;
    const html = `<!doctype html><html><head><meta charset="utf-8"/>
<title>Safety Plan Worksheet — ${esc(productName)}</title>
<style>
  body{font-family:-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;color:#1c1917;margin:32px;font-size:12px;line-height:1.5;}
  h1{font-size:18px;margin:0 0 2px;} h2{font-size:13px;margin:20px 0 6px;border-bottom:1px solid #e7e5e4;padding-bottom:4px;}
  .banner{background:#fffbeb;border:1px solid #fcd34d;color:#92400e;padding:8px 12px;border-radius:6px;font-weight:600;margin:10px 0;}
  .muted{color:#78716c;} ul{margin:4px 0 4px 18px;padding:0;}
  table{border-collapse:collapse;width:100%;margin-top:6px;} th,td{border:1px solid #e7e5e4;padding:6px 8px;text-align:left;vertical-align:top;font-size:11px;}
  th{background:#f5f5f4;} .chips span{display:inline-block;background:#f5f5f4;border:1px solid #e7e5e4;border-radius:99px;padding:1px 8px;margin:2px 4px 2px 0;font-size:11px;}
  .meta{color:#57534e;font-size:11px;margin-bottom:4px;}
</style></head><body>
  <h1>Safety Plan Worksheet</h1>
  <div class="meta">${esc(productName)} · ${esc(productType || "")}</div>
  <div class="banner">${esc(DISCLAIMER)}</div>
  <div class="meta">${reviewLine} · Generated ${esc(fmt(safetyPlan.generated_at))}</div>

  <h2>Distribution &amp; Critical Control Points</h2>
  <div class="meta">Distribution mode: ${
    safetyPlan.distribution_label ? esc(safetyPlan.distribution_label) : "not specified"
  }</div>
  ${
    (safetyPlan.critical_control_points || []).length
      ? `<p><strong>Genuine CCPs for this product</strong></p>${list(
          safetyPlan.critical_control_points
        )}`
      : `<p class="muted">No CCP identified from formulation + distribution — controls are handled as process steps / prerequisite programs. QA to confirm.</p>`
  }
  ${
    (safetyPlan.distribution_notes || []).length
      ? `<p><strong>Distribution guidance</strong></p>${list(safetyPlan.distribution_notes)}`
      : ""
  }

  <h2>1 · Allergen Control</h2>
  <div class="chips"><strong>Declared:</strong> ${
    (ac.declared_allergens || []).length
      ? (ac.declared_allergens || []).map((a) => `<span>${esc(a)}</span>`).join("")
      : '<span class="muted">none detected</span>'
  }</div>
  ${
    (ac.hidden_allergens || []).length
      ? `<div class="chips"><strong>Hidden / carried:</strong> ${(ac.hidden_allergens || [])
          .map((a) => `<span>${esc(a)}</span>`)
          .join("")}</div>`
      : ""
  }
  <p><strong>Cross-contact considerations</strong></p>${list(ac.cross_contact)}
  <p><strong>Draft control measures</strong></p>${list(ac.control_measures)}

  <h2>2 · Potential Hazards Identified From Formula</h2>
  ${
    ih.length
      ? `<table><thead><tr><th>Ingredient</th><th>Potential hazard</th></tr></thead><tbody>${ih
          .map(
            (r) =>
              `<tr><td>${esc(r.ingredient)}</td><td>${list(r.hazards)}</td></tr>`
          )
          .join("")}</tbody></table>`
      : `<p class="muted">No ingredients on file — add formulation ingredients, then regenerate.</p>`
  }

  <h2>3 · ${esc(printedAssessmentHeading)}</h2>
  <div class="meta">${
    planIsNeutral
      ? `Recorded draft inputs — pH: ${af.ph ?? "—"} · aw: ${af.water_activity ?? "—"} · Thermal: ${
          af.thermal_temp_f ? `${af.thermal_temp_f}°F` : "—"
        }${af.thermal_hold_min ? ` for ${af.thermal_hold_min} min` : ""} · Other control: ${esc(af.kill_step || "—")}`
      : planIsWet
      ? `pH: ${af.ph ?? "—"} · aw: ${af.water_activity ?? "—"} · Thermal: ${
          af.thermal_temp_f ? `${af.thermal_temp_f}°F` : "—"
        }${af.thermal_hold_min ? ` for ${af.thermal_hold_min} min` : ""}`
      : `aw: ${af.water_activity ?? "—"} · Kill step / RTE supplier control: ${esc(af.kill_step || "—")}`
  }</div>
  <p><strong>Hazards</strong></p>${list(af.hazards)}
  <p><strong>Control points to confirm</strong></p>${list(af.control_points)}
  ${af.notes && af.notes.length ? `<p><strong>Notes</strong></p>${list(af.notes)}` : ""}

  <h2>4 · Per-step Hazard Analysis</h2>
  <table><thead><tr><th>Process step</th><th>Biological</th><th>Chemical</th><th>Physical</th><th>Controls</th><th>Type</th></tr></thead>
  <tbody>${rows
    .map(
      (r) =>
        `<tr><td>${esc(r.step)}</td><td>${list(r.bio)}</td><td>${list(r.chem)}</td><td>${list(
          r.phys
        )}</td><td>${list(r.controls)}</td><td>${
          r.ccp
            ? `<strong style="color:#b91c1c;">CCP</strong>${
                r.ccp_reason ? `<br/><span class="muted">${esc(r.ccp_reason)}</span>` : ""
              }`
            : '<span class="muted">Process step</span>'
        }</td></tr>`
    )
    .join("")}</tbody></table>

  ${safetyPlan.notes ? `<h2>General notes</h2><p>${esc(safetyPlan.notes)}</p>` : ""}

  <div class="banner" style="margin-top:24px;">${esc(DISCLAIMER)} A qualified PCQI / Process Authority must validate every hazard and control before commercialization.</div>
</body></html>`;
    const w = window.open("", "_blank");
    if (!w) return toast.error("Pop-up blocked — allow pop-ups to print.");
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 250);
  };

  // ── Editable cell helpers ─────────────────────────────────────────────────
  const setAC = (k, v) =>
    setDraft((d) => ({ ...d, allergen_control: { ...d.allergen_control, [k]: v } }));
  const setAF = (k, v) =>
    setDraft((d) => ({ ...d, acidified_foods: { ...d.acidified_foods, [k]: v } }));
  const setIH = (i, k, v) =>
    setDraft((d) => {
      const ih = [...d.ingredient_hazards];
      ih[i] = { ...ih[i], [k]: v };
      return { ...d, ingredient_hazards: ih };
    });
  const addIH = () =>
    setDraft((d) => ({
      ...d,
      ingredient_hazards: [...d.ingredient_hazards, { ingredient: "", hazards: "" }],
    }));
  const removeIH = (i) =>
    setDraft((d) => ({
      ...d,
      ingredient_hazards: d.ingredient_hazards.filter((_, idx) => idx !== i),
    }));
  const setRow = (i, k, v) =>
    setDraft((d) => {
      const ha = [...d.hazard_analysis];
      ha[i] = { ...ha[i], [k]: v };
      return { ...d, hazard_analysis: ha };
    });
  const addRow = () =>
    setDraft((d) => ({
      ...d,
      hazard_analysis: [...d.hazard_analysis, { step: "", bio: "", chem: "", phys: "", controls: "" }],
    }));
  const removeRow = (i) =>
    setDraft((d) => ({ ...d, hazard_analysis: d.hazard_analysis.filter((_, idx) => idx !== i) }));

  const ta =
    "w-full text-sm border border-stone-300 rounded-md p-2 font-mono leading-snug focus:outline-none focus:ring-2 focus:ring-stone-400";

  return (
    <Card className="p-5 border-stone-200" data-testid="safety-plan-card">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between text-left"
      >
        <div className="flex items-center gap-2">
          <ShieldWarning size={20} weight="duotone" className="text-amber-600" />
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-stone-500">
              Food Safety
            </div>
            <h3 className="font-semibold">Safety Plan Worksheet</h3>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {safetyPlan && (
            <Badge variant="outline" className={meta.cls}>
              {meta.label}
            </Badge>
          )}
          <span className="text-stone-400 text-sm">{open ? "▲" : "▼"}</span>
        </div>
      </button>

      {open && (
        <div className="mt-4 space-y-5">
          {/* Disclaimer — always visible */}
          <div className="bg-amber-50 border border-amber-300 text-amber-900 text-sm rounded-md p-3">
            <strong>{DISCLAIMER}</strong> This tool pre-fills suggestions from your
            formulation data to speed up review. A qualified PCQI / Process Authority must
            validate every hazard and control before commercialization.
          </div>

          {/* Structured inputs — adapt to product type */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-[0.18em] text-stone-500">
                Process parameters
              </div>
              <span className="text-[11px] text-stone-400">
                {isCategoryNeutral
                  ? "Product-specific — no wet/dry safety class inferred"
                  : isWet
                  ? "Wet product — acidified / thermal (21 CFR 114 / 113)"
                  : "Dry / low-moisture product — preventive controls (21 CFR 117 / HARPC)"}
              </span>
            </div>

            {isCategoryNeutral ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                  <div>
                    <Label className="text-xs">Measured pH, if relevant</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={ph}
                      onChange={(e) => setPh(e.target.value)}
                      placeholder="Product-specific"
                      data-testid="input-ph"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Water activity (aw)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={waterActivity}
                      onChange={(e) => setWaterActivity(e.target.value)}
                      placeholder="Measure finished product"
                      data-testid="input-aw"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Thermal temp (°F)</Label>
                    <Input
                      type="number"
                      value={thermalTempF}
                      onChange={(e) => setThermalTempF(e.target.value)}
                      placeholder="If applicable"
                      data-testid="input-thermal-temp"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Hold time (min)</Label>
                    <Input
                      type="number"
                      value={thermalHoldMin}
                      onChange={(e) => setThermalHoldMin(e.target.value)}
                      placeholder="If applicable"
                      data-testid="input-thermal-hold"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Other validated control</Label>
                    <Input
                      value={killStep ?? ""}
                      onChange={(e) => setKillStep && setKillStep(e.target.value)}
                      placeholder="Culture, bake, supplier, etc."
                      data-testid="input-kill-step"
                    />
                  </div>
                </div>
                <p className="text-[11px] text-stone-500 mt-2">
                  These are documentation inputs only. The worksheet will not classify this
                  formula as acidified, low-acid, or low-moisture from its category. A qualified
                  reviewer must define product-specific controls, validation, packaging, and shelf life.
                </p>
              </>
            ) : isWet ? (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <Label className="text-xs">Equilibrium pH</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={ph}
                      onChange={(e) => setPh(e.target.value)}
                      placeholder="e.g. 4.2"
                      data-testid="input-ph"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Water activity (aw)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={waterActivity}
                      onChange={(e) => setWaterActivity(e.target.value)}
                      placeholder="e.g. 0.97"
                      data-testid="input-aw"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Thermal temp (°F)</Label>
                    <Input
                      type="number"
                      value={thermalTempF}
                      onChange={(e) => setThermalTempF(e.target.value)}
                      placeholder="e.g. 185"
                      data-testid="input-thermal-temp"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Hold time (min)</Label>
                    <Input
                      type="number"
                      value={thermalHoldMin}
                      onChange={(e) => setThermalHoldMin(e.target.value)}
                      placeholder="e.g. 15"
                      data-testid="input-thermal-hold"
                    />
                  </div>
                </div>
                <label className="flex items-center gap-2 mt-3 text-sm cursor-pointer">
                  <Checkbox
                    checked={!!acidified}
                    onCheckedChange={(v) => setAcidified(!!v)}
                    data-testid="checkbox-acidified"
                  />
                  Treat as an acidified food (21 CFR 114)
                </label>
              </>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Water activity (aw)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={waterActivity}
                      onChange={(e) => setWaterActivity(e.target.value)}
                      placeholder="e.g. 0.45"
                      data-testid="input-aw"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Validated kill step / RTE supplier control</Label>
                    <Input
                      value={killStep ?? ""}
                      onChange={(e) => setKillStep && setKillStep(e.target.value)}
                      placeholder="e.g. Steam-treated spices (5-log) / supplier CoA"
                      data-testid="input-kill-step"
                    />
                  </div>
                </div>
                <p className="text-[11px] text-stone-400 mt-2">
                  Dry seasonings/rubs are low-moisture — water activity and a validated
                  Salmonella kill step (or approved-supplier control) drive the hazard
                  analysis. pH / acidified (21 CFR 114) does not apply here.
                </p>
              </>
            )}

            <div className="mt-3">
              <Label className="text-xs">How is the finished product distributed?</Label>
              <select
                value={distributionMode ?? ""}
                onChange={(e) => setDistributionMode && setDistributionMode(e.target.value)}
                className="w-full md:w-auto text-sm border border-stone-300 rounded-md p-2 bg-white focus:outline-none focus:ring-2 focus:ring-stone-400"
                data-testid="select-distribution-mode"
              >
                {DISTRIBUTION_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <p className="text-[11px] text-stone-400 mt-1">
                Distribution reshapes the hazard tree — it determines which steps are
                genuine CCPs (e.g. retort → thermal + seal; ambient + acid → finished pH;
                refrigerated → cold chain).
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button onClick={generate} disabled={busy} data-testid="button-generate-safety-plan">
              <Sparkle size={16} weight="fill" className="mr-1.5" />
              {safetyPlan ? "Regenerate draft" : "Generate worksheet"}
            </Button>
            {safetyPlan && !isDemo && (
              <Button variant="outline" onClick={printWorksheet} data-testid="button-print-safety-plan">
                <Printer size={16} className="mr-1.5" />
                Print / export
              </Button>
            )}
          </div>

          {!safetyPlan && (
            <p className="text-sm text-stone-500">
              No worksheet yet. Enter your process parameters and click{" "}
              <em>Generate worksheet</em> to pre-fill a draft hazard analysis from this
              formulation's ingredients, allergens, and process flow.
            </p>
          )}

          {safetyPlan && draft && (
            <div className="space-y-6">
              <Separator />

              {/* Status / reviewer line */}
              <div className="text-sm text-stone-600">
                {status === "approved" ? (
                  <span className="inline-flex items-center gap-1.5 text-emerald-700">
                    <CheckCircle size={16} weight="fill" />
                    Approved by <strong>{safetyPlan.reviewer_name}</strong> ·{" "}
                    {fmt(safetyPlan.approved_at)}
                  </span>
                ) : (
                  <>Last updated {fmt(safetyPlan.updated_at)} · awaiting named sign-off.</>
                )}
              </div>

              {/* Distribution & CCP summary — display-only, derived by the generator */}
              <section className="rounded-md border border-stone-200 bg-stone-50 p-4">
                <h4 className="font-semibold mb-1">Distribution &amp; Critical Control Points</h4>
                <p className="text-[11px] text-stone-400 mb-3">
                  Derived from the formulation + distribution mode. Display-only — change
                  the inputs above and regenerate to update.
                </p>
                <div className="text-sm text-stone-700 mb-3">
                  <span className="text-stone-500">Distribution mode: </span>
                  {safetyPlan.distribution_label ? (
                    <Badge variant="outline" className="border-stone-300">
                      {safetyPlan.distribution_label}
                    </Badge>
                  ) : (
                    <span className="text-stone-400">not specified</span>
                  )}
                </div>
                {(safetyPlan.critical_control_points || []).length > 0 ? (
                  <div>
                    <div className="text-xs uppercase tracking-wide text-stone-500 mb-1">
                      Genuine CCPs for this product
                    </div>
                    <ul className="list-disc pl-5 space-y-1 text-sm text-stone-700">
                      {safetyPlan.critical_control_points.map((c, i) => (
                        <li key={i}>{c}</li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <p className="text-sm text-stone-500">
                    No CCP identified from formulation + distribution — controls are handled
                    as process steps / prerequisite programs. QA to confirm.
                  </p>
                )}
                {(safetyPlan.distribution_notes || []).length > 0 && (
                  <div className="mt-3">
                    <div className="text-xs uppercase tracking-wide text-stone-500 mb-1">
                      Distribution guidance
                    </div>
                    <ul className="list-disc pl-5 space-y-1 text-sm text-stone-600">
                      {safetyPlan.distribution_notes.map((n, i) => (
                        <li key={i}>{n}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </section>

              {/* 1. Allergen control */}
              <section>
                <div className="flex items-center gap-2 mb-2">
                  <ListChecks size={18} className="text-stone-600" />
                  <h4 className="font-semibold">1 · Allergen Control</h4>
                </div>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  <span className="text-xs text-stone-500 mr-1">Declared:</span>
                  {draft.allergen_control.declared_allergens.length ? (
                    draft.allergen_control.declared_allergens.map((a) => (
                      <Badge key={a} variant="secondary">
                        {a}
                      </Badge>
                    ))
                  ) : (
                    <span className="text-xs text-stone-400">none detected</span>
                  )}
                </div>
                {draft.allergen_control.hidden_allergens.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    <span className="text-xs text-amber-600 mr-1">Hidden / carried:</span>
                    {draft.allergen_control.hidden_allergens.map((a) => (
                      <Badge
                        key={a}
                        variant="outline"
                        className="border-amber-300 text-amber-800"
                      >
                        {a}
                      </Badge>
                    ))}
                  </div>
                )}
                <Label className="text-xs">Cross-contact considerations (one per line)</Label>
                <textarea
                  className={ta}
                  rows={3}
                  value={draft.allergen_control.cross_contact}
                  onChange={(e) => setAC("cross_contact", e.target.value)}
                  data-testid="textarea-cross-contact"
                />
                <Label className="text-xs mt-2 block">Draft control measures (one per line)</Label>
                <textarea
                  className={ta}
                  rows={4}
                  value={draft.allergen_control.control_measures}
                  onChange={(e) => setAC("control_measures", e.target.value)}
                  data-testid="textarea-control-measures"
                />
              </section>

              {/* 2. Potential hazards identified from the formula */}
              <section>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold">2 · Potential Hazards Identified From Formula</h4>
                  <Button variant="outline" size="sm" onClick={addIH} data-testid="button-add-ingredient-hazard">
                    <Plus size={14} className="mr-1" /> Add ingredient
                  </Button>
                </div>
                <p className="text-[11px] text-stone-400 mb-2">
                  Auto-populated from your formulation — each ingredient's allergen(s) and
                  any low-moisture pathogen risk. DRAFT; edit or remove as QA confirms.
                </p>
                {draft.ingredient_hazards.length === 0 ? (
                  <p className="text-sm text-stone-400">
                    No ingredients on file — add formulation ingredients, then regenerate.
                  </p>
                ) : (
                  <div className="overflow-hidden rounded-md border border-stone-200">
                    <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)_auto] gap-2 bg-stone-50 px-3 py-2 text-[11px] uppercase tracking-wide text-stone-500">
                      <span>Ingredient</span>
                      <span>Potential hazard (one per line)</span>
                      <span />
                    </div>
                    <div className="divide-y divide-stone-100">
                      {draft.ingredient_hazards.map((r, i) => (
                        <div
                          key={i}
                          className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)_auto] gap-2 items-start px-3 py-2"
                        >
                          <Input
                            value={r.ingredient}
                            onChange={(e) => setIH(i, "ingredient", e.target.value)}
                            placeholder="Ingredient"
                            className="font-medium"
                            data-testid={`input-ih-name-${i}`}
                          />
                          <textarea
                            className={ta}
                            rows={2}
                            value={r.hazards}
                            onChange={(e) => setIH(i, "hazards", e.target.value)}
                            data-testid={`textarea-ih-hazards-${i}`}
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeIH(i)}
                            data-testid={`button-remove-ih-${i}`}
                          >
                            <Trash size={16} className="text-stone-400" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </section>

              {/* 3. Acidified / thermal OR low-moisture / HARPC */}
              <section>
                <h4 className="font-semibold mb-2">3 · {assessmentHeading}</h4>
                <Label className="text-xs">Hazards (one per line)</Label>
                <textarea
                  className={ta}
                  rows={3}
                  value={draft.acidified_foods.hazards}
                  onChange={(e) => setAF("hazards", e.target.value)}
                  data-testid="textarea-acid-hazards"
                />
                <Label className="text-xs mt-2 block">Control points to confirm (one per line)</Label>
                <textarea
                  className={ta}
                  rows={4}
                  value={draft.acidified_foods.control_points}
                  onChange={(e) => setAF("control_points", e.target.value)}
                  data-testid="textarea-acid-controls"
                />
                {draft.acidified_foods.notes && (
                  <>
                    <Label className="text-xs mt-2 block">Notes (one per line)</Label>
                    <textarea
                      className={ta}
                      rows={2}
                      value={draft.acidified_foods.notes}
                      onChange={(e) => setAF("notes", e.target.value)}
                      data-testid="textarea-acid-notes"
                    />
                  </>
                )}
              </section>

              {/* 3. Per-step hazard analysis */}
              <section>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold">4 · Per-step Hazard Analysis</h4>
                  <Button variant="outline" size="sm" onClick={addRow} data-testid="button-add-hazard-row">
                    <Plus size={14} className="mr-1" /> Add step
                  </Button>
                </div>
                <div className="space-y-3">
                  {draft.hazard_analysis.map((r, i) => (
                    <div key={i} className="border border-stone-200 rounded-md p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <Input
                          value={r.step}
                          onChange={(e) => setRow(i, "step", e.target.value)}
                          placeholder="Process step"
                          className="font-medium"
                          data-testid={`input-step-${i}`}
                        />
                        {r.ccp ? (
                          <Badge
                            variant="outline"
                            className="border-red-300 bg-red-50 text-red-700 shrink-0"
                            data-testid={`badge-ccp-${i}`}
                          >
                            CCP
                          </Badge>
                        ) : (
                          <Badge
                            variant="outline"
                            className="border-stone-200 text-stone-400 shrink-0"
                            data-testid={`badge-step-${i}`}
                          >
                            Process step
                          </Badge>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeRow(i)}
                          data-testid={`button-remove-step-${i}`}
                        >
                          <Trash size={16} className="text-stone-400" />
                        </Button>
                      </div>
                      {r.ccp && r.ccp_reason && (
                        <p className="text-[11px] text-red-700 mb-2">{r.ccp_reason}</p>
                      )}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {[
                          ["bio", "Biological"],
                          ["chem", "Chemical"],
                          ["phys", "Physical"],
                          ["controls", "Controls"],
                        ].map(([k, lbl]) => (
                          <div key={k}>
                            <Label className="text-[11px] text-stone-500">{lbl}</Label>
                            <textarea
                              className={ta}
                              rows={2}
                              value={r[k]}
                              onChange={(e) => setRow(i, k, e.target.value)}
                              data-testid={`textarea-${k}-${i}`}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* General notes */}
              <section>
                <Label className="text-xs">General notes</Label>
                <textarea
                  className={ta}
                  rows={2}
                  value={draft.notes}
                  onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
                  data-testid="textarea-general-notes"
                />
              </section>

              <Separator />

              {/* Save + approve */}
              <div className="flex flex-wrap items-end gap-3">
                <Button variant="outline" onClick={saveEdits} disabled={busy} data-testid="button-save-safety-edits">
                  Save edits
                </Button>
                <div className="flex-1 min-w-[220px]">
                  <Label className="text-xs">Reviewer name (required to approve)</Label>
                  <Input
                    value={reviewerName}
                    onChange={(e) => setReviewerName(e.target.value)}
                    placeholder="e.g. Jane Doe, QA Manager"
                    data-testid="input-reviewer-name"
                  />
                </div>
                <Button
                  onClick={approve}
                  disabled={busy || !reviewerName.trim()}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="button-approve-safety-plan"
                >
                  <CheckCircle size={16} weight="fill" className="mr-1.5" />
                  Approve as reviewer
                </Button>
              </div>
              <p className="text-xs text-stone-500">
                Approval records the reviewer's name + timestamp. Editing the worksheet
                after approval returns it to “In review” so an approval always reflects
                reviewed content.
              </p>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
